/**
 * Eric / Vincent — Boot self-test (v3, 2026-05-19).
 *
 * Vincent a vu un "Initialisation impossible" plein écran (HTTP 520) en
 * pleine démo client. Le 520 est une erreur Cloudflare TRANSITOIRE — l'app
 * était toujours en ligne, juste un hoquet de l'edge network. L'ancienne
 * version v2 bloquait l'UI sur la première ping ratée, ce qui transformait
 * un hoquet de 5s en bug critique perçu.
 *
 * Stratégie v3 — ne JAMAIS bloquer l'utilisateur sur un hoquet réseau :
 *   1. Au démarrage : ping en arrière-plan, l'UI charge normalement.
 *   2. Si la première ping rate, on retry SILENCIEUSEMENT 3 fois avec
 *      backoff exponentiel (300ms → 800ms → 2s → 5s).
 *   3. Si toutes les retries ratent ET on a passé 8 secondes en échec
 *      consécutif, on affiche une bannière DISCRÈTE en haut, pas un écran
 *      plein. L'utilisateur peut continuer à utiliser l'app.
 *   4. La bannière disparaît seule dès qu'une ping réussit.
 *   5. L'utilisateur peut cliquer "Recharger" pour forcer un retry,
 *      mais l'app n'est jamais bloquée par cette détection.
 *
 * Couplé avec l'axios response interceptor qui retry automatiquement les
 * 502/503/504/520/521/522/523/524 sur les requêtes GET, l'utilisateur ne
 * voit plus jamais d'erreur Cloudflare temporaire.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { API_BASE } from "../lib/apiBase";

const PROBE_TIMEOUT_MS = 6000;
const BACKOFF_SEQUENCE_MS = [300, 800, 2000, 5000, 15000]; // doublé à chaque échec
const MAX_BACKOFF_MS = 30000;

// B1047 (Eric 2026-09-03) + B1050 (Eric 2026-09-03) — Détection des
// aborts causés par une NAVIGATION réelle. Version corrigée B1050 :
// on ne se fie plus à `visibilityState === "hidden"` (tab-switch
// légitime ≠ abort) ni au pattern message ambigu — uniquement au
// flag `_isNavigatingHealth` piloté par des events explicites, avec
// auto-clear 2s après pour éviter tout collage.
let _isNavigatingHealth = false;
let _healthResetTimer = null;
if (typeof window !== "undefined") {
  const _mark = () => {
    _isNavigatingHealth = true;
    if (_healthResetTimer) clearTimeout(_healthResetTimer);
    _healthResetTimer = setTimeout(() => { _isNavigatingHealth = false; }, 2000);
  };
  const _clear = () => {
    _isNavigatingHealth = false;
    if (_healthResetTimer) {
      clearTimeout(_healthResetTimer);
      _healthResetTimer = null;
    }
  };
  window.addEventListener("pagehide", _mark);
  window.addEventListener("beforeunload", _mark);
  window.addEventListener("unload", _mark);
  window.addEventListener("pageshow", (e) => {
    if (!e.persisted) _clear();
  });
}

function _isNavigationAbortErr(e) {
  // Signal unique : flag navigation piloté par event réel. Un AbortError
  // (timeout PROBE_TIMEOUT_MS) reste un vrai échec.
  if (e?.name === "AbortError") return false;
  return _isNavigatingHealth;
}

async function _probe(url) {
  const ctrl = new AbortController();
  const tmo = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
  try {
    const r = await fetch(url, {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
      signal: ctrl.signal,
    });
    clearTimeout(tmo);
    return { ok: r.status >= 200 && r.status < 400, status: r.status, url };
  } catch (e) {
    clearTimeout(tmo);
    // B1047 — Ignore les aborts navigation : ils ne prouvent RIEN sur la
    // santé du backend. On retourne `ignored: true` pour que le compteur
    // failStreak ne s'incrémente pas.
    if (_isNavigationAbortErr(e)) {
      return { ok: false, ignored: true, url, error: "navigation-abort" };
    }
    return { ok: false, status: null, url, error: String(e?.name === "AbortError" ? "timeout(6s)" : (e?.message || e)) };
  }
}

export default function BootHealthCheck({ children }) {
  // null = en train de vérifier (rare, < 500 ms)
  // true = OK
  // false = panne détectée → on affiche une bannière discrète, pas un écran plein
  const [healthy, setHealthy] = useState(null);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [lastError, setLastError] = useState(null);
  // B1044 (Eric 2026-09-02 SPEC STRICK PROD) — Se taire quand le
  // circuit-breaker global est ouvert. Contexte : sinon on empile 3
  // bannières contradictoires sur la page login ("Backend injoignable"
  // orange + "Connexion intermittente" amber + "Erreur — Vérifiez votre
  // internet" rouge). Le circuit-breaker est authoritatif : lui seul
  // parle pendant qu'il est ouvert, et jusqu'à récupération confirmée.
  const [breakerOpen, setBreakerOpen] = useState(false);
  const failStreakRef = useRef(0);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const check = useCallback(async () => {
    const primaryUrl = `${API_BASE || ""}/api/diagnostics/ping`;
    let result = await _probe(primaryUrl);

    if (!result.ok && typeof window !== "undefined" && window.location) {
      const origin = window.location.origin || "";
      if (origin && primaryUrl !== `${origin}/api/diagnostics/ping`) {
        const fallbackUrl = `${origin}/api/diagnostics/ping`;
        const r2 = await _probe(fallbackUrl);
        if (r2.ok) {
          result = r2;
        } else {
          result = { ...result, fallback_error: r2.error || `status ${r2.status}` };
        }
      }
    }

    if (!mountedRef.current) return;

    // B1047 — Un abort navigation ne prouve rien sur la santé backend :
    // on ne touche PAS au streak et on garde l'état précédent. Le prochain
    // probe naturel réévaluera.
    if (result.ignored) return;

    if (result.ok) {
      failStreakRef.current = 0;
      setHealthy(true);
      setBannerVisible(false);
      setLastError(null);
    } else {
      failStreakRef.current += 1;
      setLastError(result);
      // N'afficher la bannière qu'après 3 échecs consécutifs (~3 secondes
      // de panne réelle). Évite le flash visuel sur un seul hoquet de 1s.
      if (failStreakRef.current >= 3) {
        setHealthy(false);
        setBannerVisible(true);
      }
    }
  }, []);

  // Vérification initiale au montage + boucle de retry avec backoff exponentiel.
  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    // B1044 — synchronise avec le circuit-breaker global
    const onUnreachable = () => setBreakerOpen(true);
    const onRecovered = () => setBreakerOpen(false);
    window.addEventListener("knoria:backend-unreachable", onUnreachable);
    window.addEventListener("knoria:backend-recovered", onRecovered);

    const loop = async () => {
      while (!cancelled && mountedRef.current) {
        await check();
        if (cancelled) break;
        // Si OK, on continue à pinger mais TRÈS rarement (60s) — juste pour
        // détecter une coupure pendant une longue session.
        // Si KO, on retry rapidement avec backoff jusqu'à 30s max.
        let waitMs;
        if (failStreakRef.current === 0) {
          waitMs = 60000;
        } else {
          const idx = Math.min(failStreakRef.current - 1, BACKOFF_SEQUENCE_MS.length - 1);
          waitMs = Math.min(BACKOFF_SEQUENCE_MS[idx] || MAX_BACKOFF_MS, MAX_BACKOFF_MS);
        }
        await new Promise((r) => { timerRef.current = setTimeout(r, waitMs); });
      }
    };
    loop();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener("knoria:backend-unreachable", onUnreachable);
      window.removeEventListener("knoria:backend-recovered", onRecovered);
    };
  }, [check]);

  // L'app charge TOUJOURS — on n'affiche jamais d'écran plein bloquant.
  // Si une panne est détectée, on affiche une bannière discrète en haut.
  return (
    <>
      {bannerVisible && healthy === false && !breakerOpen && (
        <div
          className="fixed top-0 left-0 right-0 z-[9999] bg-amber-50 border-b-2 border-amber-300 px-4 py-2 flex items-center justify-between gap-3 shadow-md"
          data-testid="boot-health-banner"
        >
          <div className="flex items-center gap-2 text-amber-900 text-sm flex-1 min-w-0">
            <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v4M12 17h.01" strokeLinecap="round"/>
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeLinejoin="round"/>
            </svg>
            <span className="font-medium">Connexion intermittente avec le serveur</span>
            <span className="text-amber-700 text-xs hidden sm:inline">
              · L'app continue de fonctionner, vos données sont en sécurité. Nouvelle tentative automatique en cours…
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => {
                failStreakRef.current = 0;
                setHealthy(null);
                if (timerRef.current) clearTimeout(timerRef.current);
                check();
              }}
              className="text-xs px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded font-medium"
              data-testid="boot-health-retry-btn"
            >
              Réessayer
            </button>
            <button
              type="button"
              onClick={() => setBannerVisible(false)}
              className="text-amber-700 hover:text-amber-900 text-lg leading-none px-1"
              data-testid="boot-health-dismiss-btn"
              title="Masquer cette notification"
            >
              ×
            </button>
          </div>
        </div>
      )}
      {children}
    </>
  );
}
